/**
 * PROCESS SURVEYS COMPONENT
 *
 * Administrative interface for viewing, searching, and managing submitted home language surveys.
 * This component provides:
 *
 * - Paginated table view of all survey submissions
 * - Sortable columns for efficient data organization
 * - Authentication-protected access for school administrators
 * - Individual survey editing and updating capabilities
 * - Integration with student management systems
 * - Real-time data loading and state management
 *
 * Primary users: School administrators, district staff, state education officials
 * Purpose: Review and process home language survey data for English Learner identification
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { useRouter } from '@tanstack/react-router'
// Child components for table functionality
import SurveyList from './SurveyList'
import PaginatorNav from './PaginatorNav'
import ErrorModal from '../modals/ErrorModal'
import SurveyUpdateModal from '../modals/SurveyUpdateModal'
import { api } from '../shared/ApiProvider'

/**
 * COMPONENT ARCHITECTURE
 *
 * ProcessSurveys acts as the main container component managing state for:
 * - Table data and pagination
 * - Sorting and filtering options
 * - Modal states for editing
 * - Authentication and routing
 *
 * Data flow:
 * 1. Authentication check prevents unauthorized access
 * 2. Table configuration is memoized for performance
 * 3. Pagination state controls data fetching
 * 4. Survey data is passed to SurveyList for rendering
 * 5. Pagination controls are handled by PaginatorNav
 */
const ProcessSurveys = () => {
  const isAuthenticated = useIsAuthenticated()
  const router = useRouter()

  /**
   * TABLE COLUMN CONFIGURATION
   *
   * Defines the structure and display properties for the survey data table.
   * Uses @tanstack/react-table column definitions with custom accessors.
   *
   * Columns include:
   * - Survey ID: Unique identifier for each submission
   * - Student demographics: First name, last name, date of birth
   * - Geographic data: Country of birth
   * - School assignment: Current enrollment location
   * - System integration: PERM number for student information systems
   */
  const columns = useMemo(
    () => [
      {
        header: 'Survey ID',
        accessorKey: 'responseId',
        id: 'surveyId',
      },
      {
        header: 'First Name',
        accessorKey: 'firstName',
        id: 'firstName',
      },
      {
        header: 'Last Name',
        accessorKey: 'lastName',
        id: 'lastName',
      },
      {
        header: 'DOB',
        accessorFn: r => {
          // Format date consistently for display
          return new Date(r.birthDate).toLocaleDateString('en-US', {
            timeZone: 'UTC',
          })
        },
        id: 'dob',
      },
      // Country column removed
      {
        header: 'School',
        accessorKey: 'enrollingAt',
        id: 'school',
      },
      {
        header: 'PERM Number',
        accessorKey: 'permNumber',
        id: 'permNumber',
      },
    ],
    []
  )

  /**
   * STATE MANAGEMENT
   *
   * Complex state management for table functionality with localStorage persistence.
   * State includes:
   * - Data loading and display states
   * - Pagination controls with localStorage persistence
   * - Sorting configuration with user preferences
   *
   * The pagination state drives data fetching through useEffect hooks,
   * creating a reactive data flow that updates when users navigate or sort.
   */

  // Core table data state
  const [data, setData] = useState([]) // Survey records for current page
  const [loading, setLoading] = useState(true) // Loading indicator state

  /**
   * PAGINATION STATE WITH PERSISTENCE
   *
   * Pagination state is persisted to localStorage to maintain user preferences
   * across sessions. This improves user experience by remembering their place
   * in large datasets.
   */
  const [pageCount, setPageCount] = useState(() => {
    const saved = localStorage.getItem('pageCount')
    return saved ? parseInt(saved, 10) : 0
  })

  const [pageIndex, setPageIndex] = useState(() => {
    const saved = localStorage.getItem('pageIndex')
    return saved ? parseInt(saved, 10) : 1
  })

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('pageSize')
    return saved ? parseInt(saved, 10) : 10
  })

  // Pagination navigation state
  const [canPreviousPage, setCanPreviousPage] = useState(false)
  const [canNextPage, setCanNextPage] = useState(true)
  const [totalResults, setTotalResults] = useState(0)

  /**
   * SORTING STATE WITH PERSISTENCE
   *
   * Default sorting by response ID (newest first) with user preferences saved.
   * Changes to sorting trigger new data fetches through useEffect.
   */
  const [sortField, setSortField] = useState(() => {
    const saved = localStorage.getItem('sortField')
    // Validate that sort field is one of the valid columns
    const validFields = ['responseId', 'firstName', 'lastName', 'birthDate', 'enrollingAt', 'permNumber']
    if (saved && validFields.includes(saved)) {
      return saved
    }
    // Default to responseId if invalid or not set
    return 'responseId'
  })

  const [sortOrder, setSortOrder] = useState(() => {
    const saved = localStorage.getItem('sortOrder')
    return saved === 'asc' || saved === 'desc' ? saved : 'desc'
  })

  const [yCoord, setYCoord] = useState(() => {
    const saved = localStorage.getItem('yCoord')
    return saved ? parseInt(saved, 10) : 0
  })

  // error handling
  const [errorResponse, setErrorResponse] = useState({})
  const [isError, setIsError] = useState(false)

  // update survey modal
  const [showSurveyUpdateModal, setShowSurveyUpdateModal] = useState(false)
  const [surveyInitialVals, setSurveyInitialVals] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    birthDate: '',
    sex: '',
  })

  const fetchData = async (index, size) => {
    /*
      This is the main function that retrieves data from the database ('/survey').
      It is rerun whenever the pageIndex or pageSize are changed. Both are maintained here
      but altered in the PaginatorNav.
    */
    setIsError(false)
    setLoading(true)

    try {
      const result = await api.get('/survey', {
        params: { page: index, size: size, sort: `${sortField},${sortOrder}` },
      })

      const data = result.data.content
      const totalPages = result.data.totalPages
      const totalResultCount = result.data.totalElements

      // If we get no results, try with default sort as a fallback
      if ((!data || data.length === 0) && totalResultCount === 0 && sortField !== 'responseId') {
        const retryResult = await api.get('/survey', {
          params: { page: 1, size: size, sort: 'responseId,desc' },
        })

        if (retryResult.data.content && retryResult.data.content.length > 0) {
          setSortField('responseId')
          setSortOrder('desc')
          setPageIndex(1)
          return // Will trigger useEffect with new sort
        }
      }

      // If requested page is beyond total pages and we have pages, reset to page 1
      if (index > totalPages && totalPages > 0) {
        setPageIndex(1)
        return // Will trigger useEffect to fetch page 1
      }

      // If we have no pages but got a valid response, show empty state
      if (totalPages === 0 && totalResultCount === 0) {
        setData([])
        setPageCount(0)
        setTotalResults(0)
        setLoading(false)
        setPaginatorState(0, index)
        return
      }

      setData(data || [])
      window.scrollTo(0, yCoord)
      setPageCount(totalPages)
      setTotalResults(totalResultCount)
      setLoading(false)
      setPaginatorState(totalPages, index)
    } catch (error) {
      console.error('Error fetching data:', error)
      console.error('Error response:', error.response)
      setErrorResponse(error)
      setIsError(true)
      setLoading(false)
    }
  }

  /**
   * PAGINATION FUNCTIONS
   * For maintaining and updating pagination state
   */
  const setPaginatorState = (totalPages, currentPageIndex) => {
    if (totalPages <= 1) {
      setCanPreviousPage(false)
      setCanNextPage(false)
    } else if (currentPageIndex === 1) {
      setCanPreviousPage(false)
      setCanNextPage(true)
    } else if (currentPageIndex >= totalPages) {
      setCanNextPage(false)
      setCanPreviousPage(true)
    } else {
      setCanNextPage(true)
      setCanPreviousPage(true)
    }
  }

  const firstPage = () => {
    setPageIndex(1)
  }

  const lastPage = () => {
    setPageIndex(pageCount)
  }

  const previousPage = () => {
    if (pageIndex > 1) {
      setPageIndex(prevState => prevState - 1)
    }
  }

  const nextPage = () => {
    if (pageIndex < pageCount) {
      setPageIndex(prevState => prevState + 1)
    }
  }

  const changePageSize = selectedPageSize => {
    setPageSize(selectedPageSize)
    setPageIndex(1)
  }

  const closeErrorModal = () => {
    setIsError(false)
  }

  // Save scroll position before navigation
  const saveScrollPosition = () => {
    setYCoord(window.scrollY)
    localStorage.setItem('yCoord', window.scrollY.toString())
  }

  // Updates the information when pagesize or page index changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchData(pageIndex, pageSize)
    }
  }, [pageSize, pageIndex, sortField, sortOrder, isAuthenticated])

  // Separate effect to save state to localStorage after successful fetch
  useEffect(() => {
    if (!loading && data.length > 0) {
      localStorage.setItem('pageIndex', pageIndex.toString())
      localStorage.setItem('pageSize', pageSize.toString())
      localStorage.setItem('sortOrder', sortOrder)
      localStorage.setItem('sortField', sortField)
      localStorage.setItem('pageCount', pageCount.toString())
    }
  }, [pageIndex, pageSize, sortOrder, sortField, pageCount, loading, data])

  // Add scroll position saving effect
  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition()
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle authentication redirect
  useEffect(() => {
    if (!isAuthenticated) {
      router.navigate({ to: '/' })
    }
  }, [isAuthenticated, router])

  if (!isAuthenticated) {
    return null
  }

  return (
    <div>
      <SurveyList
        columns={columns}
        data={data}
        loading={loading}
        totalResults={totalResults}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        sortField={sortField}
        setShowSurveyUpdateModal={setShowSurveyUpdateModal}
        setSurveyInitialVals={setSurveyInitialVals}
      />
      <PaginatorNav
        pageIndex={pageIndex}
        pageCount={pageCount}
        previousPage={previousPage}
        nextPage={nextPage}
        changePageSize={changePageSize}
        firstPage={firstPage}
        lastPage={lastPage}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        loading={loading}
        pageSize={pageSize}
      />
      <SurveyUpdateModal showSurveyUpdateModal={showSurveyUpdateModal} toggleModal={setShowSurveyUpdateModal} surveyInitialVals={surveyInitialVals} />
      <ErrorModal closeErrorModal={closeErrorModal} errorResponse={errorResponse} isError={isError} />
    </div>
  )
}

export default ProcessSurveys
